import { supabase } from '../../libs/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { ForumPost, ForumComment, ForumPostWithComments, ForumCategory } from '../../types/forum';

export class ForumService {
  // Categorías del foro
  static async getCategories(): Promise<ForumCategory[]> {
    try {
      const { data, error } = await supabase
        .from('categorias_foro')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching forum categories:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCategories:', error);
      return [];
    }
  }

  // Crear categoría (solo admin)
  static async createCategory(name: string, description: string): Promise<ForumCategory | null> {
    try {
      const { data, error } = await supabase
        .from('categorias_foro')
        .insert([{ id: uuidv4(), name, description, created_at: new Date().toISOString() }])
        .select()
        .single();

      if (error) {
        console.error('Error creating category:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createCategory:', error);
      return null;
    }
  }

  // Obtener todos los posts (con comentarios)
  static async getAllPosts(limit: number = 10, offset: number = 0): Promise<ForumPostWithComments[]> {
    try {
      // Obtener posts
      const { data: posts, error: postsError } = await supabase
        .from('posteos_foro')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (postsError) {
        console.error('Error fetching forum posts:', postsError);
        return [];
      }

      if (!posts || posts.length === 0) {
        return [];
      }

      // Obtener comentarios para cada post
      const postsWithComments: ForumPostWithComments[] = [];

      for (const post of posts) {
        const { data: comments, error: commentsError } = await supabase
          .from('comentarios_foro')
          .select('*')
          .eq('post_id', post.id)
          .order('created_at', { ascending: true });

        if (commentsError) {
          console.error(`Error fetching comments for post ${post.id}:`, commentsError);
          continue;
        }

        postsWithComments.push({
          ...post,
          comments: comments || [],
          comments_count: comments ? comments.length : 0
        });
      }

      return postsWithComments;
    } catch (error) {
      console.error('Error in getAllPosts:', error);
      return [];
    }
  }

  // Obtener posts recientes (para landing page)
  static async getRecentPosts(limit: number = 4): Promise<ForumPostWithComments[]> {
    return this.getAllPosts(limit, 0);
  }

  // Crear nuevo post
  static async createPost(
    title: string,
    content: string,
    authorId: string,
    authorName: string,
    categoryId?: string,
    empresaId?: string
  ): Promise<ForumPost | null> {
    try {
      const postId = uuidv4();

      const { data, error } = await supabase
        .from('posteos_foro')
        .insert([{
          id: postId,
          title,
          content,
          author_id: authorId,
          author_name: authorName,
          created_at: new Date().toISOString(),
          category_id: categoryId,
          empresa_id: empresaId
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating post:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createPost:', error);
      return null;
    }
  }

  // Obtener post por ID (con comentarios)
  static async getPostById(postId: string): Promise<ForumPostWithComments | null> {
    try {
      const { data: post, error: postError } = await supabase
        .from('posteos_foro')
        .select('*')
        .eq('id', postId)
        .single();

      if (postError || !post) {
        console.error('Error fetching post:', postError);
        return null;
      }

      const { data: comments, error: commentsError } = await supabase
        .from('comentarios_foro')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (commentsError) {
        console.error('Error fetching comments:', commentsError);
        return null;
      }

      return {
        ...post,
        comments: comments || [],
        comments_count: comments ? comments.length : 0
      };
    } catch (error) {
      console.error('Error in getPostById:', error);
      return null;
    }
  }

  // Crear comentario
  static async createComment(
    postId: string,
    content: string,
    authorId: string,
    authorName: string
  ): Promise<ForumComment | null> {
    try {
      const { data, error } = await supabase
        .from('comentarios_foro')
        .insert([{
          id: uuidv4(),
          post_id: postId,
          content,
          author_id: authorId,
          author_name: authorName,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating comment:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createComment:', error);
      return null;
    }
  }

  // Actualizar comentario (admin o autor)
  static async updateComment(commentId: string, content: string): Promise<ForumComment | null> {
    try {
      const { data, error } = await supabase
        .from('comentarios_foro')
        .update({
          content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', commentId)
        .select()
        .single();

      if (error) {
        console.error('Error updating comment:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateComment:', error);
      return null;
    }
  }

  // Eliminar comentario (admin o autor)
  static async deleteComment(commentId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('comentarios_foro')
        .delete()
        .eq('id', commentId);

      if (error) {
        console.error('Error deleting comment:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error in deleteComment:', error);
      return false;
    }
  }

  // Actualizar post
  static async updatePost(
    postId: string,
    title: string,
    content: string
  ): Promise<ForumPost | null> {
    try {
      const { data, error } = await supabase
        .from('posteos_foro')
        .update({
          title,
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', postId)
        .select()
        .single();

      if (error) {
        console.error('Error updating post:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updatePost:', error);
      return null;
    }
  }

  // Eliminar post
  static async deletePost(postId: string): Promise<boolean> {
    try {
      // Primero eliminar comentarios
      const { error: commentsError } = await supabase
        .from('comentarios_foro')
        .delete()
        .eq('post_id', postId);

      if (commentsError) {
        console.error('Error deleting comments:', commentsError);
      }

      // Luego eliminar el post
      const { error: postError } = await supabase
        .from('posteos_foro')
        .delete()
        .eq('id', postId);

      if (postError) {
        console.error('Error deleting post:', postError);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deletePost:', error);
      return false;
    }
  }

  // Buscar posts por título o contenido
  static async searchPosts(query: string, limit: number = 10): Promise<ForumPostWithComments[]> {
    try {
      const { data: posts, error: postsError } = await supabase
        .from('posteos_foro')
        .select('*')
        .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (postsError) {
        console.error('Error searching posts:', postsError);
        return [];
      }

      if (!posts || posts.length === 0) {
        return [];
      }

      // Obtener comentarios para cada post
      const postsWithComments: ForumPostWithComments[] = [];

      for (const post of posts) {
        const { data: comments, error: commentsError } = await supabase
          .from('comentarios_foro')
          .select('*')
          .eq('post_id', post.id)
          .order('created_at', { ascending: true });

        if (commentsError) {
          console.error(`Error fetching comments for post ${post.id}:`, commentsError);
          continue;
        }

        postsWithComments.push({
          ...post,
          comments: comments || [],
          comments_count: comments ? comments.length : 0
        });
      }

      return postsWithComments;
    } catch (error) {
      console.error('Error in searchPosts:', error);
      return [];
    }
  }

  // Obtener posts por categoría
  static async getPostsByCategory(
    categoryId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<ForumPostWithComments[]> {
    try {
      const { data: posts, error: postsError } = await supabase
        .from('posteos_foro')
        .select('*')
        .eq('category_id', categoryId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (postsError) {
        console.error('Error fetching posts by category:', postsError);
        return [];
      }

      if (!posts || posts.length === 0) {
        return [];
      }

      // Obtener comentarios para cada post
      const postsWithComments: ForumPostWithComments[] = [];

      for (const post of posts) {
        const { data: comments, error: commentsError } = await supabase
          .from('comentarios_foro')
          .select('*')
          .eq('post_id', post.id)
          .order('created_at', { ascending: true });

        if (commentsError) {
          console.error(`Error fetching comments for post ${post.id}:`, commentsError);
          continue;
        }

        postsWithComments.push({
          ...post,
          comments: comments || [],
          comments_count: comments ? comments.length : 0
        });
      }

      return postsWithComments;
    } catch (error) {
      console.error('Error in getPostsByCategory:', error);
      return [];
    }
  }

  // Obtener posts por autor
  static async getPostsByAuthor(
    authorId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<ForumPostWithComments[]> {
    try {
      const { data: posts, error: postsError } = await supabase
        .from('posteos_foro')
        .select('*')
        .eq('author_id', authorId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (postsError) {
        console.error('Error fetching posts by author:', postsError);
        return [];
      }

      if (!posts || posts.length === 0) {
        return [];
      }

      // Obtener comentarios para cada post
      const postsWithComments: ForumPostWithComments[] = [];

      for (const post of posts) {
        const { data: comments, error: commentsError } = await supabase
          .from('comentarios_foro')
          .select('*')
          .eq('post_id', post.id)
          .order('created_at', { ascending: true });

        if (commentsError) {
          console.error(`Error fetching comments for post ${post.id}:`, commentsError);
          continue;
        }

        postsWithComments.push({
          ...post,
          comments: comments || [],
          comments_count: comments ? comments.length : 0
        });
      }

      return postsWithComments;
    } catch (error) {
      console.error('Error in getPostsByAuthor:', error);
      return [];
    }
  }
}
